import React from 'react'
import {Form} from '../../../common/hooks/useForm'
import {resolveCoordinates} from '../../../common/utils/resolveCoordinates'
import {slottsskogenPosition} from '../../../common/utils/slottsskogenPosition'
import {Box} from '../../../webui/Box'
import {TextField, TextFieldProps} from '../../../webui/inputs/TextField'
import {Typography} from '../../../webui/Typography'
import {EditableMap} from '../map/EditableMap'
import {LocationTranslationsForm} from '../add/AddLocation'
import {
  LOCATION_TRANSLATION_LANGUAGE_LABELS,
  NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES,
  NonCanonicalLocationTranslationLanguage,
} from '../translationLanguages'

interface EditLocationProps {
  form: EditLocationForm
  updateField: Form<EditLocationForm>['updateField']
}

export interface EditLocationForm {
  description?: string
  lat?: string
  long?: string
  locality?: string
  municipality?: string
  name?: string
  translations?: LocationTranslationsForm
}

export const EditLocation: React.FC<EditLocationProps> = ({form, updateField}) => {
  const onNameChange: TextFieldProps['onChange'] = (value) => {
    updateField('name', value)
  }

  const onDescriptionChange: TextFieldProps['onChange'] = (value) => {
    updateField('description', value)
  }

  const onLatChange: TextFieldProps['onChange'] = (value) => {
    updateField('lat', value)
  }

  const onLongChange: TextFieldProps['onChange'] = (value) => {
    updateField('long', value)
  }

  const onLocalityChange: TextFieldProps['onChange'] = (value) => {
    updateField('locality', value)
  }

  const onPositionChange = (lat: number, long: number) => {
    updateField('lat', String(lat))
    updateField('long', String(long))
  }

  const onTranslationDescriptionChange =
    (lang: NonCanonicalLocationTranslationLanguage): TextFieldProps['onChange'] =>
    (value) => {
      if (!form.translations) return
      updateField('translations', {
        ...form.translations,
        [lang]: {
          ...form.translations[lang],
          description: value,
        },
      })
    }

  const coordinates = resolveCoordinates(form.lat, form.long)

  return (
    <Box>
      <Typography variant="h5">Location</Typography>
      <Box display="flex" flexWrap="wrap">
        <Box mr={1} mt={1}>
          <TextField label="Name" onChange={onNameChange} value={form.name} />
        </Box>
        <Box mr={1} mt={1}>
          <TextField label="English Description" onChange={onDescriptionChange} value={form.description} />
        </Box>
        <Box mr={1} mt={1}>
          <TextField label="Latitude" onChange={onLatChange} value={form.lat} />
        </Box>
        <Box mr={1} mt={1}>
          <TextField label="Longitude" onChange={onLongChange} value={form.long} />
        </Box>
      </Box>

      <Box display="flex" flexWrap="wrap">
        {/* Oct 2025 - Municipality seems to not be used. (One more location, search for 2389ry293r8y*)/}
        {/*<Box mr={1} mt={1}>*/}
        {/*  <TextField*/}
        {/*    label="Municipality"*/}
        {/*    value={form.municipality ?? ''}*/}
        {/*    disabled*/}
        {/*    InputProps={{*/}
        {/*      endAdornment: (*/}
        {/*        <InputAdornment position="end">*/}
        {/*          <Tooltip title={"We don't use this field"}>*/}
        {/*            <IconButton size="small" tabIndex={-1}>*/}
        {/*              <HelpOutlineIcon fontSize="small" />*/}
        {/*            </IconButton>*/}
        {/*          </Tooltip>*/}
        {/*        </InputAdornment>*/}
        {/*      ),*/}
        {/*    }}*/}
        {/*  />*/}
        {/*</Box>*/}

        <Box mr={1} mt={1}>
          <TextField label="City" onChange={onLocalityChange} value={form.locality ?? ''} />
        </Box>
      </Box>

      {(() => {
        const translations = form.translations
        if (!translations) return null
        return (
          <Box mt={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              Description translations
            </Typography>
            <Typography variant="body2" sx={{mb: 1, opacity: 0.8}}>
              Translations for additional languages. Optional — missing languages fall back to English (the Description above).
            </Typography>
            {NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES.map((lang) => (
              <Box key={lang} mt={1.5} p={1.5} border="1px solid rgba(0,0,0,0.08)" borderRadius={4}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {LOCATION_TRANSLATION_LANGUAGE_LABELS[lang]}
                </Typography>
                <Box mt={0.5}>
                  <Typography variant="body2">Description</Typography>
                  <TextField
                    id={`edit-location-translation-${lang}-description`}
                    value={translations[lang].description}
                    onChange={onTranslationDescriptionChange(lang)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )
      })()}

      <Box mt={2}>
        <EditableMap
          initialMarkerPosition={coordinates ? [coordinates.lat, coordinates.long] : slottsskogenPosition}
          onPositionChange={onPositionChange}
        />
      </Box>
    </Box>
  )
}

export default EditLocation
